/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatDateTime, presentScanNoteVisibility, presentScanNoteVisibilityHint } from '@kaiizen/kai-presenters';
import {
  canUserDeleteScanNote,
  canUserEditScanNote,
  type FlattenedApiScanNote,
  getScanNoteVisibility,
  isAuthoredRemarkScanNote,
  type ScanNoteVisibility,
} from '@kaiizen/kai-pro-api';
import { useAuth, useScanEditingContextSlice, useScanNotesMutators, useUsers } from '@kaiizen/kai-pro-api-react-state';
import type { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { getGroupTitle, getTaskTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import {
  buildScopeNoteOriginIndex,
  countScopeNotesByOrigin,
  formatScopeNotesCount,
  getScopeNotesForLevel,
  SCOPE_NOTE_ORIGIN_LABEL,
  type ScopeNoteEntry,
  type ScopeNotesLevel,
} from '@/Client/Lib/scopeNotesLib';
import { NOTES_CHARACTER_LIMIT } from '@/Client/noteConstants';
import { useNotesDrawerOptional, useToast } from '@/Client/State';
import { SideDrawer } from '@/Client/Widgets/SideDrawer';
import { InfoCircleIcon, RightIcon } from '@kaiizen/kai-shiki/react';
import { DEFAULT_SCOPE_NOTES_DRAWER_STYLES, type ScopeNotesDrawerStyles } from './scopeNotesDrawerStyles';

// Single source of truth for the drawer's own copy — tests reference these rather than retyping them.
export const SCOPE_NOTES_DRAWER_SAVE_LABEL = 'Save note';
export const SCOPE_NOTES_DRAWER_EDIT_LABEL = 'Edit';
export const SCOPE_NOTES_DRAWER_DELETE_LABEL = 'Delete';
export const SCOPE_NOTES_DRAWER_CONFIRM_DELETE_LABEL = 'Confirm delete';
export const SCOPE_NOTES_DRAWER_KEEP_LABEL = 'Keep';
export const SCOPE_NOTES_DRAWER_CANCEL_EDIT_LABEL = 'Cancel';
export const SCOPE_NOTES_DRAWER_SAVE_EDIT_LABEL = 'Save';
export const SCOPE_NOTES_DRAWER_EDITED_LABEL = 'Edited';
export const SCOPE_NOTES_DRAWER_EMPTY_TEXT = 'No notes yet.';
export const SCOPE_NOTES_DRAWER_UNKNOWN_AUTHOR = 'Someone';

const levelNoun = (level: ScopeNotesLevel) => level.kind;

export const getScopeNotesComposerPlaceholder = (level: ScopeNotesLevel, visibility: ScanNoteVisibility) =>
  visibility === 'internal'
    ? `Add an internal note about this ${levelNoun(level)}…`
    : `Add an external note about this ${levelNoun(level)}…`;

/** The level the scope navigator is pointing at — the drawer's default, and the deepest it offers. */
const resolveNavigatorLevel = (
  scanRoot: ScanRootOptimized,
  selectedGroupId: string | undefined,
  selectedTaskId: string | undefined,
): ScopeNotesLevel => {
  if (selectedTaskId) {
    // A contractor route carries a task but no group, so the task's group is found by looking.
    const group = scanRoot.groups?.find(candidate =>
      (!selectedGroupId || candidate.id === selectedGroupId) && candidate.tasks?.some(task => task.id === selectedTaskId)
    );
    if (group) return { kind: 'task', groupId: group.id, taskId: selectedTaskId };
  }
  if (selectedGroupId && scanRoot.groups?.some(group => group.id === selectedGroupId)) return { kind: 'group', groupId: selectedGroupId };
  return { kind: 'scope' };
};

const levelKey = (level: ScopeNotesLevel) => level.kind === 'scope' ? 'scope' : level.kind === 'group' ? `group:${level.groupId}` : `task:${level.taskId}`;

/** Every level from the scope down to the navigator's selection — the breadcrumb trail. */
const getAvailableLevels = (navigatorLevel: ScopeNotesLevel): ScopeNotesLevel[] => {
  if (navigatorLevel.kind === 'scope') return [navigatorLevel];
  const group: ScopeNotesLevel = { kind: 'group', groupId: navigatorLevel.groupId };
  return navigatorLevel.kind === 'group' ? [{ kind: 'scope' }, group] : [{ kind: 'scope' }, group, navigatorLevel];
};

const getLevelTitle = (scanRoot: ScanRootOptimized, level: ScopeNotesLevel): string => {
  if (level.kind === 'scope') return SCOPE_NOTE_ORIGIN_LABEL;
  const group = scanRoot.groups?.find(candidate => candidate.id === level.groupId);
  if (!group) return '';
  if (level.kind === 'group') return getGroupTitle(group);
  const task = group.tasks?.find(candidate => candidate.id === level.taskId);
  return task ? getTaskTitle(task) : '';
};

const getLevelScanPartId = (scanRoot: ScanRootOptimized, level: ScopeNotesLevel): string =>
  level.kind === 'scope' ? scanRoot.id : level.kind === 'group' ? level.groupId : level.taskId;

const ScopeNoteCard = ({
  entry,
  showOrigin,
  authorName,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  styles,
}: {
  entry: ScopeNoteEntry;
  showOrigin: boolean;
  authorName: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (body: string) => Promise<void>;
  onDelete: () => Promise<void>;
  styles: ScopeNotesDrawerStyles;
}) => {
  const { note, origin } = entry;
  const visibility = getScanNoteVisibility(note);
  const [mode, setMode] = useState<'view' | 'edit' | 'confirmDelete'>('view');
  const [editDraft, setEditDraft] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const edited = ((note as FlattenedApiScanNote).history?.length ?? 0) > 1;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      setMode('view');
    } finally {
      setBusy(false);
    }
  };

  const editChanged = editDraft.trim() !== '' && editDraft.trim() !== note.body.trim();

  return (
    <article css={[styles.note, styles.noteByVisibility[visibility]]} data-testid={'scope-note'} data-note-id={note.id} data-visibility={visibility}>
      <div css={styles.noteMeta}>
        <span css={styles.noteAuthor}>{authorName}</span>
        <span css={styles.noteWhen}>
          {formatDateTime(note.createdAt)}
          {edited ? ` · ${SCOPE_NOTES_DRAWER_EDITED_LABEL}` : ''}
        </span>
        {mode === 'view' && (canEdit || canDelete)
          ? (
            <span css={styles.noteActions}>
              {canEdit
                ? (
                  <button
                    type={'button'}
                    css={styles.textButton}
                    onClick={() => {
                      setEditDraft(note.body);
                      setMode('edit');
                    }}
                  >
                    {SCOPE_NOTES_DRAWER_EDIT_LABEL}
                  </button>
                )
                : null}
              {canDelete
                ? (
                  <button type={'button'} css={[styles.textButton, styles.dangerTextButton]} onClick={() => setMode('confirmDelete')}>
                    {SCOPE_NOTES_DRAWER_DELETE_LABEL}
                  </button>
                )
                : null}
            </span>
          )
          : null}
      </div>
      {mode === 'edit'
        ? (
          <>
            <textarea
              css={styles.textarea}
              aria-label={'Edit note'}
              rows={3}
              maxLength={NOTES_CHARACTER_LIMIT}
              value={editDraft}
              autoFocus
              onChange={event => setEditDraft(event.target.value)}
            />
            <div css={styles.confirmRow}>
              <button
                type={'button'}
                css={styles.confirmButton}
                disabled={busy || !editChanged}
                onClick={() => run(() => onEdit(editDraft.trim()))}
              >
                {SCOPE_NOTES_DRAWER_SAVE_EDIT_LABEL}
              </button>
              <button type={'button'} css={styles.confirmButton} disabled={busy} onClick={() => setMode('view')}>
                {SCOPE_NOTES_DRAWER_CANCEL_EDIT_LABEL}
              </button>
            </div>
          </>
        )
        : <div css={styles.noteBody}>{note.body.trim()}</div>}
      <div css={styles.chips}>
        <span css={styles.visibilityChipByVisibility[visibility]} data-testid={'scope-note-visibility'}>{presentScanNoteVisibility(visibility)}</span>
        {showOrigin ? <span css={styles.chip} data-testid={'scope-note-origin'}>{origin.label}</span> : null}
      </div>
      {mode === 'confirmDelete'
        ? (
          <div css={styles.confirmRow}>
            <button type={'button'} css={[styles.confirmButton, styles.dangerConfirmButton]} disabled={busy} onClick={() => run(onDelete)}>
              {SCOPE_NOTES_DRAWER_CONFIRM_DELETE_LABEL}
            </button>
            <button type={'button'} css={styles.confirmButton} disabled={busy} onClick={() => setMode('view')}>
              {SCOPE_NOTES_DRAWER_KEEP_LABEL}
            </button>
          </div>
        )
        : null}
    </article>
  );
};

/**
 * The notes drawer for the scope editor: the notes at one level of the scope — scope, group or
 * task — and a composer for adding to it.
 *
 * Not a modal. It sits over the right of the workspace without a scrim, so the scope navigator
 * stays usable while it's open, and it follows the navigator: selecting another group or task
 * retargets it. The breadcrumb trail lets a note be filed (and read) at any level above that
 * selection; selecting something else in the navigator returns the drawer to the new selection.
 * It stays open until its own close button is pressed.
 */
export const ScopeNotesDrawer = ({
  selectedGroupId,
  selectedTaskId,
  styles = DEFAULT_SCOPE_NOTES_DRAWER_STYLES,
}: {
  selectedGroupId?: string;
  selectedTaskId?: string;
  styles?: ScopeNotesDrawerStyles;
}) => {
  const drawer = useNotesDrawerOptional();
  const { scanId, scanRoot, scanNotesByScanPartId, scanIsEditable } = useScanEditingContextSlice([
    'scanId',
    'scanRoot',
    'scanNotesByScanPartId',
    'scanIsEditable',
  ]);
  // The scan-scoped mutator stamps the author and scan itself — the same write path every other note takes.
  const scanNoteMutators = useScanNotesMutators(scanId);
  if (!drawer?.open || !scanRoot) return null;
  return (
    <ScopeNotesDrawerContent
      scanRoot={scanRoot}
      scanNotesByScanPartId={scanNotesByScanPartId ?? {}}
      createScanNote={scanNoteMutators.create}
      canWrite={!!scanIsEditable}
      selectedGroupId={selectedGroupId}
      selectedTaskId={selectedTaskId}
      composerFocusRequest={drawer.composerFocusRequest}
      scopeNotesRequest={drawer.scopeNotesRequest}
      onClose={drawer.closeNotesDrawer}
      styles={styles}
    />
  );
};

const ScopeNotesDrawerContent = ({
  scanRoot,
  scanNotesByScanPartId,
  createScanNote,
  canWrite,
  selectedGroupId,
  selectedTaskId,
  composerFocusRequest,
  scopeNotesRequest,
  onClose,
  styles,
}: {
  scanRoot: ScanRootOptimized;
  scanNotesByScanPartId: Record<string, FlattenedApiScanNote[]>;
  createScanNote: ReturnType<typeof useScanNotesMutators>['create'];
  canWrite: boolean;
  selectedGroupId?: string;
  selectedTaskId?: string;
  composerFocusRequest: number;
  scopeNotesRequest: number;
  onClose: () => void;
  styles: ScopeNotesDrawerStyles;
}) => {
  const { userId, role, orgName } = useAuth();
  const { usersById } = useUsers();
  const { showErrorToast } = useToast();

  const navigatorLevel = useMemo(
    () => resolveNavigatorLevel(scanRoot, selectedGroupId, selectedTaskId),
    [scanRoot, selectedGroupId, selectedTaskId],
  );
  const navigatorKey = levelKey(navigatorLevel);

  // A breadcrumb choice holds only for the selection it was made against — select something else
  // in the navigator and the drawer follows it again.
  const [chosenLevel, setChosenLevel] = useState<{ forNavigatorKey: string; level: ScopeNotesLevel }>();
  const level = chosenLevel?.forNavigatorKey === navigatorKey ? chosenLevel.level : navigatorLevel;
  const availableLevels = getAvailableLevels(navigatorLevel);
  const appliedScopeNotesRequest = useRef(0);

  useEffect(() => {
    if (scopeNotesRequest === appliedScopeNotesRequest.current) return;
    appliedScopeNotesRequest.current = scopeNotesRequest;
    setChosenLevel({ forNavigatorKey: navigatorKey, level: { kind: 'scope' } });
  }, [navigatorKey, scopeNotesRequest]);

  const originIndex = useMemo(() => buildScopeNoteOriginIndex(scanRoot), [scanRoot]);
  const entries = useMemo(
    () => getScopeNotesForLevel(scanRoot, scanNotesByScanPartId, level, originIndex),
    [scanRoot, scanNotesByScanPartId, level, originIndex],
  );
  const countLabel = formatScopeNotesCount(level, countScopeNotesByOrigin(entries));

  const [draft, setDraft] = useState('');
  const [visibility, setVisibility] = useState<ScanNoteVisibility>('internal');
  const [saving, setSaving] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (composerFocusRequest > 0) composerRef.current?.focus();
  }, [composerFocusRequest]);

  const canSave = canWrite && !saving && draft.trim() !== '';
  const saveNote = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createScanNote({ scanPartId: getLevelScanPartId(scanRoot, level), scanNoteType: 'info', body: draft.trim(), visibility });
      setDraft('');
    } catch (error) {
      showErrorToast(error, 'save the note');
    } finally {
      setSaving(false);
    }
  };

  const actor = { userId, role };

  return (
    <SideDrawer
      open
      onClose={onClose}
      ariaLabel={'Notes'}
      closeLabel={'Close notes'}
      testId={'scope-notes-drawer'}
      bodyTestId={'scope-notes-list'}
      styles={styles.drawer}
      header={
        <>
          {availableLevels.length > 1
            ? (
              <nav css={styles.crumbs} aria-label={'Note level'}>
                {availableLevels.map((candidate, index) => {
                  const current = levelKey(candidate) === levelKey(level);
                  return (
                    <span key={levelKey(candidate)} css={styles.crumb}>
                      {index > 0 ? <RightIcon aria-hidden={'true'} size={10} /> : null}
                      <button
                        type={'button'}
                        css={[styles.crumbButton, current && styles.currentCrumbButton]}
                        aria-current={current ? 'true' : undefined}
                        onClick={() => setChosenLevel({ forNavigatorKey: navigatorKey, level: candidate })}
                      >
                        {getLevelTitle(scanRoot, candidate)}
                      </button>
                    </span>
                  );
                })}
              </nav>
            )
            : null}
          <h2 css={styles.title}>{getLevelTitle(scanRoot, level)}</h2>
          {countLabel ? <div css={styles.count} data-testid={'scope-notes-count'}>{countLabel}</div> : null}
        </>
      }
      footer={canWrite
        ? (
          <div css={styles.composer}>
            <textarea
              ref={composerRef}
              css={styles.textarea}
              rows={4}
              maxLength={NOTES_CHARACTER_LIMIT}
              aria-label={'New note'}
              placeholder={getScopeNotesComposerPlaceholder(level, visibility)}
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void saveNote();
                }
              }}
            />
            <div css={styles.composerRow}>
              <div css={styles.visibilityToggle} role={'group'} aria-label={'Note visibility'}>
                {(['internal', 'external'] as const).map(option => (
                  <button
                    key={option}
                    type={'button'}
                    css={[styles.visibilityOption, visibility === option && styles.selectedVisibilityOptionByVisibility[option]]}
                    aria-pressed={visibility === option}
                    onClick={() => setVisibility(option)}
                  >
                    {presentScanNoteVisibility(option)}
                  </button>
                ))}
              </div>
              <span
                css={styles.hint}
                role={'img'}
                aria-label={presentScanNoteVisibilityHint(visibility, orgName)}
                title={presentScanNoteVisibilityHint(visibility, orgName)}
              >
                <InfoCircleIcon size={16} aria-hidden={'true'} />
              </span>
              <button type={'button'} css={styles.saveButton} disabled={!canSave} title={'Save note (⌘↵)'} onClick={() => void saveNote()}>
                {SCOPE_NOTES_DRAWER_SAVE_LABEL}
              </button>
            </div>
          </div>
        )
        : undefined}
    >
      {entries.length === 0
        ? <div css={styles.empty}>{SCOPE_NOTES_DRAWER_EMPTY_TEXT}</div>
        : entries.map(entry => {
          const remark = isAuthoredRemarkScanNote(entry.note);
          return (
            <ScopeNoteCard
              key={entry.note.id}
              entry={entry}
              showOrigin={level.kind !== 'task'}
              authorName={usersById?.[entry.note.userId]?.name ?? SCOPE_NOTES_DRAWER_UNKNOWN_AUTHOR}
              canEdit={canWrite && remark && canUserEditScanNote(actor, entry.note)}
              canDelete={canWrite && remark && canUserDeleteScanNote(actor, entry.note)}
              styles={styles}
              onEdit={async body => {
                try {
                  await createScanNote({ scanPartId: entry.note.scanPartId, scanNoteType: 'edit', parentScanNoteId: entry.note.id, body });
                } catch (error) {
                  showErrorToast(error, 'edit the note');
                }
              }}
              onDelete={async () => {
                try {
                  await createScanNote({ scanPartId: entry.note.scanPartId, scanNoteType: 'delete', parentScanNoteId: entry.note.id, body: '' });
                } catch (error) {
                  showErrorToast(error, 'delete the note');
                }
              }}
            />
          );
        })}
    </SideDrawer>
  );
};
